// API Service for handling data updates via update-data.ashx endpoint

const baseAPIURL = process.env.REACT_APP_BASE_API_URL || '';
const API_URL = `${baseAPIURL}/api/update-data.ashx`;

const sanitizeUpdateData = (payload = {}) => {
  const sanitized = {};

  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (value !== undefined && value !== null) {
      sanitized[key] = value;
    }
  });

  return sanitized;
};

export const apiService = {
  /**
   * Upload a file to the server
   */
  async uploadImage(file, onProgress) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${baseAPIURL}/api/upload.ashx`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && typeof onProgress === 'function') {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        try {
          const result = JSON.parse(xhr.responseText || '{}');
          if (xhr.status >= 200 && xhr.status < 300 && result.success) {
            resolve(result);
            return;
          }

          reject(new Error(result.message || 'Image upload failed'));
        } catch (error) {
          reject(new Error('Image upload failed'));
        }
      };

      xhr.onerror = () => reject(new Error('Image upload failed'));
      xhr.send(formData);
    });
  },

  /**
   * Fetch all data from the server
   */
  async getData() {
    try {
      const response = await fetch(`${baseAPIURL}/data.ashx`);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  /**
   * Update data on the server
   * @param {Object} updateData - Object containing articles, teamMembers, services, gallery, or carouselImages to update
   */
  async updateData(updateData) {
    try {
      // First get the existing data
      const existingData = await this.getData();
      const sanitizedUpdate = sanitizeUpdateData(updateData);

      // Merge the updates with existing data while ignoring null/undefined values.
      const mergedData = {
        carouselImages: sanitizedUpdate.carouselImages !== undefined ? sanitizedUpdate.carouselImages : existingData.carouselImages,
        articles: sanitizedUpdate.articles !== undefined ? sanitizedUpdate.articles : existingData.articles,
        teamMembers: sanitizedUpdate.teamMembers !== undefined ? sanitizedUpdate.teamMembers : existingData.teamMembers,
        services: sanitizedUpdate.services !== undefined ? sanitizedUpdate.services : existingData.services,
        gallery: sanitizedUpdate.gallery !== undefined ? sanitizedUpdate.gallery : existingData.gallery,
        contact: sanitizedUpdate.contact !== undefined ? sanitizedUpdate.contact : existingData.contact,
        about: sanitizedUpdate.about !== undefined ? sanitizedUpdate.about : existingData.about,
      };

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: mergedData
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Update failed');
      }
      return result;
    } catch (error) {
      console.error('Error updating data:', error);
      throw error;
    }
  },

  /**
   * Update team members
   */
  async updateTeamMembers(members) {
    return this.updateData({
      teamMembers: members,
      articles: undefined,
      carouselImages: undefined,
      services: undefined,
      gallery: undefined,
    });
  },

  /**
   * Update articles
   */
  async updateArticles(articles) {
    return this.updateData({
      articles: articles,
      teamMembers: undefined,
      carouselImages: undefined,
      services: undefined,
      gallery: undefined,
    });
  },

  /**
   * Update carousel banners
   */
  async updateBanners(images) {
    return this.updateData({
      carouselImages: images,
      articles: undefined,
      teamMembers: undefined,
      services: undefined,
      gallery: undefined,
    });
  },

  /**
   * Update services
   */
  async updateServices(services) {
    return this.updateData({
      services: services,
      articles: undefined,
      teamMembers: undefined,
      carouselImages: undefined,
      gallery: undefined,
    });
  },

  /**
   * Update gallery
   */
  async updateGallery(gallery) {
    return this.updateData({
      gallery: gallery,
      articles: undefined,
      teamMembers: undefined,
      carouselImages: undefined,
      services: undefined,
      contact: undefined,
    });
  },

  /**
   * Update site contact info and social links
   */
  async updateContact(contact) {
    return this.updateData({
      contact: contact,
      articles: undefined,
      teamMembers: undefined,
      carouselImages: undefined,
      services: undefined,
      gallery: undefined,
      about: undefined,
    });
  },

  /**
   * Update About page content
   */
  async updateAbout(about) {
    return this.updateData({
      about: about,
      articles: undefined,
      teamMembers: undefined,
      carouselImages: undefined,
      services: undefined,
      gallery: undefined,
      contact: undefined,
    });
  }
};
